/**
 * CustomerHistoryPage — full-screen interaction history viewer for the
 * standalone Customer History pop-out window.
 *
 * Rendered by the `?ch=1` branch in `main.tsx` after the API client has been
 * configured for the operator. This page fills the entire OS window
 * (100vw × 100vh) and renders `CompleteHistoryTab` flush to the edges so the
 * operator gets maximum space for reading the customer's email history.
 *
 * Layout:
 *   ┌──────────────────────────────────┐
 *   │  Header  (customer name / email) ~52px flexShrink:0
 *   ├──────────────────────────────────┤
 *   │                                  │
 *   │  CompleteHistoryTab              flex:1 (manages its own scroll)
 *   │                                  │
 *   └──────────────────────────────────┘
 */

import type { CSSProperties, ReactNode } from 'react'
import { Mail } from 'lucide-react'
import { CompleteHistoryTab } from '@/features/inbox-manager/components/CompleteHistoryTab'

export interface CustomerHistoryPageProps {
  customerEmail: string | null
  customerId: string | null
  customerName: string | null
  /**
   * Fill the parent element instead of the viewport (Task 4094).
   *
   * The desktop pop-out owns a whole OS window, so the default `100vh × 100vw`
   * is right there. The browser portal renders this same page inside an in-page
   * panel, where viewport units would overflow the panel's own box. Setting
   * this switches the root to `100%`, leaving every other style — and the whole
   * body — identical. The page is not forked for the two hosts.
   */
  embedded?: boolean
}

export function CustomerHistoryPage({
  customerEmail,
  customerId,
  customerName,
  embedded = false,
}: CustomerHistoryPageProps): ReactNode {
  const displayName = customerName ?? customerEmail ?? customerId ?? 'Customer'
  const subtitle    = customerName ? (customerEmail ?? customerId ?? null) : null

  return (
    <div style={embedded ? { ...styles.root, ...styles.rootEmbedded } : styles.root}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerIcon}>
          <Mail style={{ width: '16px', height: '16px', color: 'var(--text-muted)' }} />
        </div>
        <div style={styles.headerText}>
          <span style={styles.headerName}>{displayName}</span>
          {subtitle && (
            <span style={styles.headerSubtitle}>{subtitle}</span>
          )}
        </div>
      </div>

      {/* Body — CompleteHistoryTab owns its own internal scroll */}
      <div style={styles.body}>
        <CompleteHistoryTab
          customerEmail={customerEmail ?? undefined}
          customerId={customerId ?? undefined}
          currentInteractionId=""
        />
      </div>
    </div>
  )
}

const styles: Record<string, CSSProperties> = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100vw',
    overflow: 'hidden',
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)',
  },
  /** Overrides for the in-page panel host — see the `embedded` prop. */
  rootEmbedded: {
    height: '100%',
    width: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 20px',
    borderBottom: '1px solid var(--border)',
    flexShrink: 0,
    background: 'var(--bg-secondary)',
  },
  headerIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    background: 'var(--bg-tertiary)',
    flexShrink: 0,
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    minWidth: 0,
  },
  headerName: {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerSubtitle: {
    fontSize: '12px',
    color: 'var(--text-muted)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  body: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
}
